/* eslint-disable react/no-array-index-key */
import {
  Box,
  Chip,
  Icon,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { useCareMatrix } from 'hooks/useCareMatrix';
import { getPatientMoodTrackerAtDay } from 'services/user';

import { factorsValues, feelingsValues, moodValues, moods } from './utils';

import { DayDetailsProps } from './types';

import * as S from './styles';

export function DayDetails({ date, onClose }: DayDetailsProps) {
  const intl = useIntl();
  const { patientCognitoId } = useCareMatrix();

  const { data: moodAtThisDay } = useQuery({
    queryKey: ['getPatientMoodTrackerAtDay', patientCognitoId, date],
    queryFn: () => getPatientMoodTrackerAtDay(patientCognitoId, date),
  });

  function handleClose() {
    onClose();
  }

  return (
    <S.Card variant="outlined" id="card">
      <Box
        component="header"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body2" fontWeight="medium">
          {intl.formatMessage(
            { id: 'CAREMATRIXCPTC.MOODTRACKERTITLE.DATE' },
            {
              date: date.toLocaleString(intl.locale, {
                day: '2-digit',
                month: '2-digit',
              }),
            },
          )}
        </Typography>

        <IconButton onClick={handleClose}>
          <S.CloseIcon />
        </IconButton>
      </Box>

      {moodAtThisDay ? (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            {moods[moodAtThisDay.mood]}
            <Typography variant="caption" color="text.secondary">
              {intl.formatMessage(
                {
                  id: 'CAREMATRIXCPTC.MOODTRACKERSUBTITLE.CURRENTMOOD',
                },
                {
                  chosenmood: intl.formatMessage({
                    id: moodValues[moodAtThisDay.mood],
                  }),
                },
              )}
            </Typography>
          </Box>

          {!!moodAtThisDay.feelings?.length && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2" fontWeight={500}>
                {intl.formatMessage({
                  id: 'CAREMATRIXCPTC.MOODTRACKERTITLE.FEELINGS',
                })}
              </Typography>

              <Box display="flex" gap={1} flexWrap="wrap">
                {moodAtThisDay.feelings?.map(feeling => {
                  return (
                    <Chip
                      key={feeling}
                      label={intl.formatMessage({
                        id: feelingsValues[feeling],
                      })}
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {!!moodAtThisDay.factors?.length && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2" fontWeight={500}>
                {intl.formatMessage({
                  id: 'CAREMATRIXCPTC.MOODTRACKERTITLE.DUETO',
                })}
              </Typography>

              <Box display="flex" gap={1} flexWrap="wrap">
                {moodAtThisDay.factors?.map(factor => {
                  return (
                    <Chip
                      key={factor}
                      label={intl.formatMessage({ id: factorsValues[factor] })}
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {moodAtThisDay?.note && (
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Icon>
                <S.QuotesIcon />
              </Icon>
              <Typography
                variant="caption"
                display="flex"
                color="text.secondary"
              >
                {moodAtThisDay.note}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Skeleton height={20} width={20} variant="circular" />
            <Skeleton height={20} width={210} variant="text" />
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2" fontWeight={500}>
              {intl.formatMessage({
                id: 'CAREMATRIXCPTC.MOODTRACKERTITLE.FEELINGS',
              })}
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap">
              {Array.from({ length: 5 }).map((_, index) => {
                return <Skeleton width={82} key={index} height={24} />;
              })}
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2" fontWeight={500}>
              {intl.formatMessage({
                id: 'CAREMATRIXCPTC.MOODTRACKERTITLE.DUETO',
              })}
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap">
              {Array.from({ length: 3 }).map((_, index) => {
                return <Skeleton width={82} key={index} height={24} />;
              })}
            </Box>
          </Box>

          <Box display="flex" alignItems="flex-start" gap={1}>
            <Icon>
              <S.QuotesIcon />
            </Icon>
            <Box display="flex" flexDirection="column" alignItems="center">
              {Array.from({ length: 3 }).map((_, index) => (
                <Skeleton key={index} width={300} height={20} variant="text" />
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </S.Card>
  );
}
